<!-- eslint-disable vue/multi-word-component-names -->
<script setup lang="ts">
import {useRootStore} from '@/store';
import {Alert, CurrentUserViewModel, Modal} from '@/store/classes';
import {ExternalFacilityType} from '@/store/enums';
import axios from 'axios';
import {computed, onMounted, Ref, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import FormField from "@/components/common/FormField.vue3.vue"
import { z } from 'zod';
import { useFormValidation } from '@/components/common/FormValidation';
import { RequiredStringSchema } from '@/components/common/schemas';

const props = withDefaults(defineProps<{
    portalLogin?: boolean;
}>(), {
    portalLogin: false
});

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const rootStore = useRootStore();

const username = ref('');
const password = ref('');
const facilityType: Ref<ExternalFacilityType> = ref(ExternalFacilityType.NhMessaging);

const setAccessToken = (token: string) => rootStore.dispatch('setAccessToken', token);
const setStaffId = (id?: number) => rootStore.dispatch('setStaffId', id);
const setPatientId = (id?: number) => rootStore.dispatch('setPatientId', id);
const setFacilityId = (id: number) => rootStore.dispatch('setFacilityId', id);

const showAlert = (alert: Alert) => rootStore.dispatch('showAlert', alert);
const showLoader = (loaderName: string) => rootStore.dispatch('showLoader', loaderName);
const hideLoader = (loaderName: string) => rootStore.dispatch('hideLoader', loaderName);
const showModal = async (modal: Modal) => rootStore.dispatch('showModal', modal);
const initialize = async () => rootStore.dispatch('initialize');
const initializePatientPortal = async () => rootStore.dispatch('initializePatientPortal');

const facilityTypes = [
    { text: t('enums.ExternalFacilityType.TherapyC'), value: ExternalFacilityType.TherapyC }, 
    { text: t('enums.ExternalFacilityType.XfitTherapy'), value: ExternalFacilityType.XfitTherapy },
    { text: t('enums.ExternalFacilityType.XfitWoundCare'), value: ExternalFacilityType.XfitWoundCare },
    { text: t('enums.ExternalFacilityType.NhMessaging'), value: ExternalFacilityType.NhMessaging }
];

const usernameRef: Ref<HTMLInputElement | null> = ref(null)

const Schema = z.object({ username: RequiredStringSchema, password: RequiredStringSchema})
const passwordModel = computed(() => ({ username: username.value, password: password.value }))
const validationState = useFormValidation<z.infer<typeof Schema>>(Schema, passwordModel, true);
const isStandaloneLogin = computed(() => 
    facilityType.value === ExternalFacilityType.NhPortal 
    || facilityType.value === ExternalFacilityType.NhMessaging 
    || facilityType.value === ExternalFacilityType.TherapyC
)

const resetPasswordUrl = computed(() =>
    facilityType.value === ExternalFacilityType.XfitTherapy 
        ? `${process.env.VUE_APP_XFIT_THERAPY_BASE_URL}/Security/ResetPassword`
        : facilityType.value === ExternalFacilityType.XfitWoundCare
            ? `${process.env.VUE_APP_XFIT_WOUNDCARE_BASE_URL}/Security/ResetPassword`
            : ''
)

onMounted(async () => {
    facilityType.value = props.portalLogin ? ExternalFacilityType.NhPortal
        : (route.query?.facilityType as ExternalFacilityType || ExternalFacilityType.NhMessaging);
    usernameRef.value?.focus(); 
    if (route.query.invalidToken) {
        await showAlert({ text: 'Unable to log in, please try again', type: 'error' });
    }
});


async function login(): Promise<void> {
    if (!validationState.validate()) {
        return;
    }
            
    try {
        await showLoader('login');
        
        const authResponse = await axios({
            url: isStandaloneLogin.value ? '/auth/messaging' : '/auth/sso',
            method: 'post',
            data: {
                username: username.value,
                password: password.value,
                applicationName: facilityTypeToAppName(facilityType.value)
            }
        });
                
        if (!authResponse.data) {
            await showAlert({ text: 'Unable to log in, please try again', type: 'error' });
            return;
        }
                
        await setAccessToken(authResponse.data);
                
        const user = (await axios.get<CurrentUserViewModel>('/auth/user/current', { params: getFacilityAuthParams() })).data;
        const facilityId = await getUserFacilityId(user);
        if (!facilityId) {
            await showAlert({ text: 'No facility selected, please try again', type: 'error' });
            return;
        }

        if (props.portalLogin) {
            await Promise.all([
                setPatientId(user.contact.id),
                setFacilityId(facilityId),
            ]);
            await initializePatientPortal();
            await router.push('/home');
        } else {
            await Promise.all([
                setStaffId(user.contact.id),
                setFacilityId(facilityId),
            ]);
            await initialize();
            await router.push('/alerts/messages');
        }
    } catch (e: any) {
        console.error(e);
        await showAlert({ 
            text: ((e?.response?.status === 401 && e.response.data)
                ? e.response.data
                : 'An error has occurred, please try again'), 
            type: 'error' 
        });
    } finally {
        await hideLoader('login');
    }
}

function getFacilityAuthParams(): { facilityIdEnc?: string; facilityId?: number} {
    const facilityIdParam = (route.query.facilityId || route.query.facility) as string;
    if (!isNaN(facilityIdParam as any)) {
        return { facilityId: parseInt(facilityIdParam) }
    } else {
        return { facilityIdEnc: facilityIdParam }
    }
}

async function getUserFacilityId(user: CurrentUserViewModel): Promise<number | undefined> {
    let facilityId;

    if (user.facilities.length === 1 || props.portalLogin) {
        facilityId = user.facilities[0].id;
    } else if (user.facilities.length > 1) {
        await hideLoader('login');
        facilityId = await showModal({ name: 'FacilitySelectModal', props: { facilities: user.facilities } });
        await showLoader('login');
    }

    return facilityId;
}

function facilityTypeToAppName(facilityType: ExternalFacilityType): string | null {
    switch (facilityType) {
    case ExternalFacilityType.XfitTherapy:
        return 'ReDoc';
    case ExternalFacilityType.XfitWoundCare:
        return 'WoundExpert';
    case ExternalFacilityType.NhMessaging:
        return 'NhMessaging';
    case ExternalFacilityType.TherapyC:
        return 'NhMessaging';
    case ExternalFacilityType.NhPortal:
        return 'NhPortal';
    default:
        return null;
    }
}
const openResetPassword = () => window.open(resetPasswordUrl.value, '_blank');
</script>

<template>
    <div class="flex">
        <div class="hidden md:flex brand-panel min-h-screen">
            <span v-if="portalLogin"> Your Access To <br> Better Care. </span>
            <span v-else> Reuniting Caregivers With <br> Their Calling. </span>
        </div>
        <div class="min-h-screen flex align-items-center justify-content-center">
            <div class="flex justify-content-center align-items-center w-full">
                <form-field class="p-0 sm:col-9 lg:col-7 xl:col-5">
                    <img
                        v-if="portalLogin"
                        src="img/patient-portal-long-color.png"
                        alt="Net Health Patient Portal"
                        class="pb-6 max-w-full"
                    >
                    <img
                        v-else
                        src="img/logo_wide_color.png"
                        alt="Net Health Patient Messaging"
                        class="pb-6 max-w-full"
                    >
                    <form-field
                        v-slot="{ onBlur }"
                        :validation-state="validationState.result.value.username"
                        label-key="labels.user.username"
                    >
                        <p-input-text 
                            v-model="username"
                            autofocus
                            class="w-full"
                            @blur="onBlur"
                        />
                    </form-field>

                    <form-field
                        v-slot="{ onBlur }"
                        :validation-state="validationState.result.value.password"
                        label-key="labels.user.password"
                    >
                        <p-input-text
                            v-model="password"
                            class="w-full"
                            type="password"
                            @blur="onBlur"
                        />
                    </form-field>

                    <form-field 
                        v-if="!props.portalLogin"
                        label-key="labels.user.application"
                    >
                        <p-dropdown
                            v-model="facilityType"
                            :options="facilityTypes"
                            class="w-full"
                            option-label="text"
                            option-value="value"
                        />
                    </form-field>

                    <div class="flex justify-content-end gap-2">
                        <p-button
                            v-if="isStandaloneLogin"
                            outlined
                            @click="showModal({ name: 'ResetLoginModal' })"
                        >
                            {{ t('actions.resetPassword') }}
                        </p-button>
                        <p-button
                            v-else
                            outlined
                            @click="openResetPassword"
                        >
                            {{ t('actions.resetPassword') }}
                        </p-button>
                        <p-button @click.prevent="login">
                            {{ t('actions.login') }}
                        </p-button>
                    </div>
                </form-field>
            </div>
            <div class="copyright-bar font-weight-light">
                <div v-if="portalLogin" class="flex justify-content-center mb-2">
                    <router-link to="/terms-and-conditions">
                        Terms and Conditions of Use
                    </router-link>
                    <div class="mx-2">
                        |
                    </div>
                    <router-link to="/privacy-policy">
                        Privacy Policy
                    </router-link>
                </div>
                <div>
                    Copyright &copy; {{ new Date().getFullYear() }} - Net Health. All Rights Reserved.
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
:deep(.copyright-bar) {
    position: fixed;
    bottom: 0; 
}

:deep(.brand-panel) {
    background-color: #f9423a;
    font-family: 'NotoSerif';
    font-size: 72px;
    color: white;
    height: 100%;
    letter-spacing: -2px;
    display: inline-block;
    min-width: 425px;
    max-width: 425px;
    line-height: 1.2;
    span {
        display: flex;
        padding: 0 25px 25px 25px;
        align-items: center;
    }
}
</style>
